import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AiChatOpenedVia } from '../../analytics/types/analytics-events.ts';
import { useAskPlatoSpeaking } from '../avatar/use-ask-plato-speaking.ts';
import { useAskPlatoChat } from '../hooks/use-ask-plato-chat.ts';
import { useAskPlatoEnabled } from '../hooks/use-ask-plato-enabled.ts';
import { AskPlatoPanel } from './ask-plato-panel.tsx';
import { AskPlatoSurface } from './ask-plato-surface.tsx';

type AskPlatoContextType = {
    open: boolean;
    openAskPlato: (openedVia: AiChatOpenedVia) => void;
    closeAskPlato: () => void;
    toggleAskPlato: (openedVia: AiChatOpenedVia) => void;
    /** Opens the chat and sends `question` as the start of a fresh conversation. */
    askPlato: (question: string, openedVia: AiChatOpenedVia) => void;
    /**
     * Opens a fresh conversation with `before` + `after` in the composer and
     * the cursor between them, for a question the user still has to complete.
     */
    draftAskPlato: (draft: { before: string; after: string }, openedVia: AiChatOpenedVia) => void;
    /**
     * Whether the teaser bubble — and the unread dot, they are one state — is
     * still live. Both show until the user opens the chat or dismisses the
     * teaser; either action removes both, for good.
     */
    showTeaser: boolean;
    dismissTeaser: () => void;
    /** New words are streaming in; the avatar moves its mouth. */
    speaking: boolean;
};

const AskPlatoContext = createContext<AskPlatoContextType | undefined>(undefined);

export function useAskPlato(): AskPlatoContextType {
    const context = useContext(AskPlatoContext);
    if (!context) {
        throw new Error('useAskPlato must be used within an AskPlatoProvider');
    }
    return context;
}

type DraftRequest = {
    /** Remounts the panel, so each request seeds the composer once. */
    id: number;
    text: string;
    caret: number;
};

type PanelSession = {
    open: boolean;
    openedVia: AiChatOpenedVia;
};

/**
 * Set once the user has opened the chat or dismissed the teaser. Until it is
 * present, the teaser bubble and the unread dot invite first use.
 */
const teaserDismissedStorageKey = 'ask_plato_teaser_dismissed';

/**
 * A conversation closed longer ago than this is stale: opening the chat again
 * starts a fresh conversation instead of growing the old one's context. The
 * old conversation stays in the history.
 */
const staleConversationAfterMs = 20 * 60 * 1000;

/**
 * Owns the open state of the Ask Plato chat, the teaser/unread-dot state, and
 * the ⌘J / Ctrl+J shortcut.
 *
 * The desktop popover is non-modal: it can be opened from anywhere and stays
 * open while the user keeps navigating.
 *
 * Feature behind `enableAiChat`: outside it there is no chat and no shortcut.
 * The entry points are gated by their own render sites.
 */
export function AskPlatoProvider({ children }: PropsWithChildren): ReactElement {
    const askPlatoEnabled = useAskPlatoEnabled();
    const sendEvent = useAnalytics();
    // Mirrors the surface: below `sm` the chat is a full-screen takeover.
    const isDesktop = useBreakpoint('sm');
    const [open, setOpen] = useState(false);
    const [teaserDismissed, setTeaserDismissed] = useState(
        () => localStorage.getItem(teaserDismissedStorageKey) !== null,
    );
    const chat = useAskPlatoChat({ open });
    const { startNewChat, sendMessage } = chat;
    const speaking = useAskPlatoSpeaking(chat);
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const sessionRef = useRef<PanelSession>({ open: false, openedVia: 'fab' });
    const closedAtRef = useRef<number | undefined>(undefined);
    const [draftRequest, setDraftRequest] = useState<DraftRequest | undefined>(undefined);

    useEffect(() => {
        if (askPlatoEnabled) {
            sendEvent('ai_chat_entry_point_rendered');
        }
    }, [askPlatoEnabled, sendEvent]);

    const dismissTeaser = useCallback(() => {
        setTeaserDismissed(true);
        localStorage.setItem(teaserDismissedStorageKey, 'true');
    }, []);

    // Every path in and out of the chat goes through here — the two entry
    // points, the teaser, the shortcut, and the surface's own close button via
    // the dialog primitive's `onOpenChange` — so the open/close pair is
    // reported once, and the close reports the entry point that opened it.
    const setPanelOpen = useCallback(
        (next: boolean, openedVia: AiChatOpenedVia): void => {
            const session = sessionRef.current;
            if (session.open === next) {
                return;
            }

            if (next) {
                sessionRef.current = { open: true, openedVia };
                sendEvent('ai_chat_opened', { opened_via: openedVia });
                // Opening the chat retires the teaser and the unread dot for good.
                dismissTeaser();
                if (
                    closedAtRef.current !== undefined &&
                    Date.now() - closedAtRef.current > staleConversationAfterMs
                ) {
                    startNewChat('stale');
                }
            } else {
                sessionRef.current = { ...session, open: false };
                closedAtRef.current = Date.now();
                // The panel unmounts on close; the next open starts empty.
                setDraftRequest(undefined);
                sendEvent('ai_chat_closed', { opened_via: session.openedVia });
            }

            setOpen(next);
        },
        [sendEvent, dismissTeaser, startNewChat],
    );

    useEffect(() => {
        if (!askPlatoEnabled) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'j' || !(event.metaKey || event.ctrlKey)) {
                return;
            }
            event.preventDefault();
            setPanelOpen(!sessionRef.current.open, 'shortcut');
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [askPlatoEnabled, setPanelOpen]);

    const openAskPlato = useCallback(
        (openedVia: AiChatOpenedVia) => {
            setPanelOpen(true, openedVia);
        },
        [setPanelOpen],
    );

    // A question from outside the panel is a new topic — another customer,
    // another figure — so it starts its own conversation; the previous one
    // stays in the history. Clearing, opening and sending all run through
    // refs, so the order holds within this call.
    const askPlato = useCallback(
        (question: string, openedVia: AiChatOpenedVia) => {
            startNewChat('home_card', { interrupt: true });
            setPanelOpen(true, openedVia);
            sendMessage(question);
        },
        [startNewChat, setPanelOpen, sendMessage],
    );

    const draftAskPlato = useCallback(
        ({ before, after }: { before: string; after: string }, openedVia: AiChatOpenedVia) => {
            startNewChat('home_card', { interrupt: true });
            setDraftRequest((previous) => ({
                id: (previous?.id ?? 0) + 1,
                text: `${before}${after}`,
                caret: before.length,
            }));
            setPanelOpen(true, openedVia);
        },
        [startNewChat, setPanelOpen],
    );

    const closeAskPlato = useCallback(() => {
        setPanelOpen(false, sessionRef.current.openedVia);
    }, [setPanelOpen]);

    const toggleAskPlato = useCallback(
        (openedVia: AiChatOpenedVia) => {
            setPanelOpen(!sessionRef.current.open, openedVia);
        },
        [setPanelOpen],
    );

    const value = useMemo(
        () => ({
            open: askPlatoEnabled && open,
            openAskPlato,
            closeAskPlato,
            toggleAskPlato,
            askPlato,
            draftAskPlato,
            showTeaser: askPlatoEnabled && !teaserDismissed,
            dismissTeaser,
            speaking,
        }),
        [
            askPlatoEnabled,
            open,
            openAskPlato,
            closeAskPlato,
            toggleAskPlato,
            askPlato,
            draftAskPlato,
            teaserDismissed,
            dismissTeaser,
            speaking,
        ],
    );

    return (
        <AskPlatoContext.Provider value={value}>
            {askPlatoEnabled && (
                <AskPlatoSurface
                    open={open}
                    onOpenChange={(next) => {
                        setPanelOpen(next, sessionRef.current.openedVia);
                    }}
                    // Opening the chat means wanting to ask something, so focus
                    // the composer rather than the close button the primitive
                    // would pick. Touch keeps the default, where focusing a
                    // textarea would raise the virtual keyboard over the panel.
                    initialFocus={(openType) => (openType === 'touch' ? true : composerRef.current)}
                >
                    <AskPlatoPanel
                        key={draftRequest?.id}
                        initialDraft={draftRequest}
                        chat={chat}
                        composerRef={composerRef}
                        // The desktop popover stays put over the new page; the
                        // mobile takeover would hide it, so it closes like any close.
                        onPageLinkNavigated={() => {
                            if (!isDesktop) {
                                closeAskPlato();
                            }
                        }}
                    />
                </AskPlatoSurface>
            )}
            {children}
        </AskPlatoContext.Provider>
    );
}
